import { motion } from 'framer-motion'

// Section card wrapper
export function AdminCard({ title, subtitle, children, action }) {
  return (
    <div className="glass-card rounded-2xl border border-white/5 overflow-hidden">
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
        <div>
          <h3 className="font-display font-semibold text-white">{title}</h3>
          {subtitle && <p className="text-xs text-slate-500 mt-0.5 font-mono">{subtitle}</p>}
        </div>
        {action}
      </div>
      <div className="p-6">{children}</div>
    </div>
  )
}

// Form field
export function Field({ label, children, hint }) {
  const renderLabel = (text) => {
    if (typeof text !== 'string') return text;
    if (text.includes('*')) {
      const parts = text.split('*');
      return (
        <>
          {parts[0]}<span className="text-red-500 font-bold ml-0.5">*</span>{parts.slice(1).join('*')}
        </>
      );
    }
    return text;
  };

  return (
    <div className="mb-4">
      <label className="block font-mono text-xs text-slate-500 mb-1.5 tracking-widest uppercase">
        {renderLabel(label)}
      </label>
      {children}
      {hint && <p className="text-xs text-slate-700 mt-1 font-mono">{hint}</p>}
    </div>
  )
}

// Text input
export function Input({ value, onChange, placeholder, type = 'text', maxLength = 300, disabled, required }) {
  return (
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      maxLength={maxLength}
      disabled={disabled}
      required={required}
      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-slate-200 placeholder-slate-600 outline-none transition-all focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/20 disabled:opacity-50"
    />
  )
}

// Textarea
export function Textarea({ value, onChange, placeholder, rows = 3, maxLength = 1000 }) {
  return (
    <textarea
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={rows}
      maxLength={maxLength}
      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-slate-200 placeholder-slate-600 outline-none transition-all focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/20 resize-none"
    />
  )
}

// Select
export function Select({ value, onChange, options }) {
  return (
    <select
      value={value}
      onChange={onChange}
      className="w-full bg-dark-700 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-slate-200 outline-none transition-all focus:border-blue-500/50 appearance-none"
    >
      {options.map(opt => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  )
}

// Toggle switch
export function Toggle({ checked, onChange, label }) {
  return (
    <label className="flex items-center gap-3 cursor-pointer">
      <div
        onClick={() => onChange(!checked)}
        className={`relative w-10 h-5 rounded-full transition-all duration-300 ${checked ? '' : 'bg-white/10'}`}
        style={checked ? { background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)' } : {}}
      >
        <motion.div
          className="absolute top-0.5 w-4 h-4 bg-white rounded-full shadow"
          animate={{ x: checked ? 20 : 2 }}
          transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        />
      </div>
      {label && <span className="text-sm text-slate-300">{label}</span>}
    </label>
  )
}

// Primary button
export function BtnPrimary({ onClick, children, disabled, small }) {
  return (
    <motion.button
      onClick={onClick}
      disabled={disabled}
      className={`btn-primary text-white flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${small ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm'}`}
      whileHover={!disabled ? { scale: 1.03 } : {}}
      whileTap={!disabled ? { scale: 0.97 } : {}}
    >
      {children}
    </motion.button>
  )
}

// Outline button
export function BtnOutline({ onClick, children, disabled, small, danger }) {
  return (
    <motion.button
      onClick={onClick}
      disabled={disabled}
      className={`btn-outline flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
        danger ? 'text-red-400 border-red-500/20 hover:border-red-500/40 hover:bg-red-500/5' : 'text-slate-300'
      } ${small ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm'}`}
      whileHover={!disabled ? { scale: 1.02 } : {}}
      whileTap={!disabled ? { scale: 0.97 } : {}}
    >
      {children}
    </motion.button>
  )
}

// Toast notification
export function Toast({ message, type = 'success' }) {
  const colors = {
    success: 'bg-green-500/10 border-green-500/20 text-green-400',
    error: 'bg-red-500/10 border-red-500/20 text-red-400',
    info: 'bg-blue-500/10 border-blue-500/20 text-blue-400',
  }
  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -10 }}
      className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-xl border font-mono text-sm ${colors[type]}`}
    >
      {message}
    </motion.div>
  )
}

// Range slider for skill level
export function SkillRange({ value, onChange }) {
  return (
    <div className="flex items-center gap-3">
      <input
        type="range"
        min={0}
        max={100}
        value={value}
        onChange={onChange}
        className="flex-1 accent-blue-500"
      />
      <span className="font-mono text-xs text-slate-400 w-8 text-right">{value}%</span>
    </div>
  )
}

// Color badge picker
export function ColorPicker({ value, onChange }) {
  const colors = ['blue', 'purple', 'cyan', 'green', 'orange']
  const hex = { blue: '#3B82F6', purple: '#8B5CF6', cyan: '#06B6D4', green: '#10B981', orange: '#F59E0B' }
  return (
    <div className="flex gap-2">
      {colors.map(c => (
        <button
          key={c}
          type="button"
          onClick={() => onChange(c)}
          className="w-6 h-6 rounded-full border-2 transition-all"
          style={{
            background: hex[c],
            borderColor: value === c ? '#fff' : 'transparent',
            transform: value === c ? 'scale(1.2)' : 'scale(1)',
          }}
          title={c}
        />
      ))}
    </div>
  )
}

// Stat badge
export function StatBadge({ value, label, color = 'blue' }) {
  const colors = {
    blue: 'bg-blue-500/10 border-blue-500/20 text-blue-400',
    purple: 'bg-purple-500/10 border-purple-500/20 text-purple-400',
    green: 'bg-green-500/10 border-green-500/20 text-green-400',
    cyan: 'bg-cyan-500/10 border-cyan-500/20 text-cyan-400',
  }
  return (
    <div className={`px-3 py-2 rounded-xl border text-center ${colors[color]}`}>
      <p className="font-display font-bold text-xl">{value}</p>
      <p className="text-xs opacity-70 mt-0.5">{label}</p>
    </div>
  )
}
